/**
 * O QUE UM MODAL PRECISA TER ALÉM DE APARECER.
 *
 * Medido nos modais de Processos em 13/09/2026, abrindo o de importar com o
 * teclado: sem `role="dialog"`, sem `aria-modal`, sem nome, o FOCO FICOU NO
 * BOTÃO ATRÁS DELE e o Escape não fechava.
 *
 * O que isso significa na prática para quem não usa mouse: o modal abre, a
 * pessoa aperta Tab e continua andando pela página de trás — que está coberta
 * e invisível. Ela não tem como chegar nos campos, não tem como fechar, e o
 * leitor de tela não avisou que alguma coisa abriu.
 *
 * As cinco coisas moram aqui e não em cada modal porque são sempre as mesmas,
 * e porque modal escrito à mão esquece uma delas — normalmente a devolução do
 * foco, que é a que ninguém testa.
 *
 * POR QUE DIRETIVA E NÃO COMPOSABLE (14/09/2026)
 * A primeira versão era `useModalAcessivel(ref, aberto, aoFechar)`: uma `ref`
 * no elemento, um booleano e uma chamada, três coisas para manter em sincronia
 * por modal. Na ficha do processo são DEZENOVE modais — 57 pedaços de
 * cerimônia, e cada um deles uma chance de esquecer o terceiro.
 *
 * `v-if` já monta e desmonta o elemento nos exatos momentos de abrir e fechar,
 * que é o que o booleano dizia. A diretiva usa esses momentos:
 *
 *     <div class="advup-modal" v-modal-acessivel="fecharIsso">
 *
 * O valor é o que fazer no Escape (opcional: sem ele o Escape não fecha, mas
 * o resto continua valendo).
 */
import { guardarModal } from './guardaDoModal';

const FOCAVEIS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// 🔴 NÃO USAR `offsetParent` PARA DIZER SE ESTÁ VISÍVEL.
//
// Era o que eu tinha escrito, e o teste pegou: `offsetParent` é nulo tanto
// para quem está escondido quanto para QUALQUER elemento `position: fixed`
// — e modal é fixo. Também é sempre nulo onde não existe layout, o que
// tornava a armadilha de Tab impossível de testar.
//
// `display`/`visibility`/`hidden` dizem a mesma coisa sem depender de o
// navegador já ter desenhado a tela.
const estaVisivel = e => {
  if (e.hasAttribute('hidden') || e.disabled) return false;
  // Bloco inerte (o modal atrás da pergunta "Descartar?") não recebe foco.
  if (e.closest('[inert]')) return false;
  const s = getComputedStyle(e);
  return s.display !== 'none' && s.visibility !== 'hidden';
};

const focaveisDe = caixa =>
  [...caixa.querySelectorAll(FOCAVEIS)].filter(estaVisivel);

const darNome = caixa => {
  caixa.setAttribute('role', 'dialog');
  // A folha AO LADO (`data-nao-modal`, 29/10/2026) não é modal: a página
  // atrás continua viva e usável.
  caixa.setAttribute(
    'aria-modal',
    caixa.hasAttribute('data-nao-modal') ? 'false' : 'true'
  );

  // O nome do diálogo vem do próprio título que já está na tela. Sem isto o
  // leitor anuncia só "diálogo", e a pessoa não sabe qual.
  if (caixa.getAttribute('aria-labelledby')) return;
  const titulo = caixa.querySelector('.advup-modal__titulo, h1, h2, h3');
  if (!titulo) return;
  if (!titulo.id) {
    titulo.id = `modal-tit-${Math.random().toString(36).slice(2, 9)}`;
  }
  caixa.setAttribute('aria-labelledby', titulo.id);
};

const entrarComOFoco = caixa => {
  // A FICHA DE LEITURA começa pelo TÍTULO. Sem campo, a regra de baixo punha
  // o foco no primeiro link — na ficha rápida da agenda, o link da pasta, com
  // o anel de foco desenhado nele ao abrir (Victor, 14/09/2026). Quem marca
  // `data-foco-inicial` diz onde a leitura começa; o leitor de tela anuncia o
  // título, e o Tab segue para o primeiro controle.
  const marcado = caixa.querySelector('[data-foco-inicial]');
  if (marcado) {
    if (!marcado.hasAttribute('tabindex'))
      marcado.setAttribute('tabindex', '-1');
    marcado.focus();
    return;
  }

  const lista = focaveisDe(caixa);

  // ONDE O FOCO ENTRA. Quem abre um formulário quer ESCREVER.
  //
  // Duas medições ensinaram a ordem. No modal de Casos (13/09/2026) o foco
  // caía no X, que é o primeiro do DOM em todo modal do produto — a pessoa
  // começava pelo botão de desistir. Excluí o X, e no modal de compromisso
  // (14/09/2026) o foco passou a cair na ABA ativa, que vem antes dos campos.
  //
  // Então: campo primeiro; se o diálogo não tiver campo nenhum — um "confirma
  // excluir?" —, o primeiro botão que não seja o X, que nesses diálogos é o
  // "Cancelar". O X e as abas continuam a um Tab de distância.
  // Campo de ARQUIVO não é onde se escreve: costuma estar escondido dentro da
  // área de soltar, que ganhava o anel de foco ao abrir (04/10/2026).
  // O SELETOR DO PRODUTO (`AdvupSelect`) é um botão, e a regra acima o
  // pulava: no Transferir o foco caía no comentário opcional, o 3º campo,
  // em vez do time (05/10/2026). Quem embrulha um campo em
  // `data-campo-inicial` diz que é por ali que o formulário começa.
  const pedido = lista.find(e => e.closest('[data-campo-inicial]'));
  const campo =
    pedido ??
    lista.find(
      e => /^(INPUT|SELECT|TEXTAREA)$/.test(e.tagName) && e.type !== 'file'
    );
  const primeiro =
    campo ??
    lista.find(e => !e.classList.contains('advup-modal__fechar')) ??
    lista[0];

  if (primeiro) {
    primeiro.focus();
    return;
  }
  caixa.setAttribute('tabindex', '-1');
  caixa.focus();
};

// O estado de cada modal aberto. Num `WeakMap` e não numa propriedade do
// elemento: o elemento é do template, e pendurar campo nele é sujeira que
// sobrevive ao desmonte.
const abertos = new WeakMap();

export const modalAcessivel = {
  mounted(caixa, binding) {
    const focoAnterior = document.activeElement;

    // O que impede o modal de levar embora o que a pessoa escreveu.
    const guarda = guardarModal(caixa, {
      t: binding.instance?.$t?.bind(binding.instance),
    });

    const aoTeclar = evento => {
      // FOLHA AO LADO (`data-nao-modal`, 29/10/2026 — a gaveta de assinatura
      // com a conversa nítida ao lado): Escape e Tab só valem com o foco
      // DENTRO dela. Quem está escrevendo na conversa não pode ter a folha
      // fechada pelo Escape nem o Tab puxado de volta para ela.
      if (
        caixa.hasAttribute('data-nao-modal') &&
        !caixa.contains(evento.target)
      )
        return;
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        // Com algo escrito, o Escape pergunta antes (guardaDoModal.js).
        if (binding.value && guarda.aoEscape(() => binding.value())) return;
        binding.value?.();
        return;
      }

      if (evento.key !== 'Tab') return;

      // A ARMADILHA DE TAB. Sem ela o Tab sai do modal pela última posição e
      // continua na página de trás — que está coberta. A pessoa "perde" a
      // tela. Ela INCLUI o X: fora da armadilha ele ficaria inalcançável.
      const lista = focaveisDe(caixa);
      if (!lista.length) return;
      const primeiro = lista[0];
      const ultimo = lista[lista.length - 1];
      const atual = document.activeElement;

      if (evento.shiftKey && (atual === primeiro || !caixa.contains(atual))) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && atual === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    };

    darNome(caixa);

    // O CONTEÚDO DO MODAL NEM SEMPRE EXISTE QUANDO ELE MONTA.
    //
    // Medido no modal de compromisso da ficha (14/09/2026): um quadro depois
    // de montar, a caixa tinha o X e as sete abas, e NENHUM dos 18 campos —
    // eles dependem de dados que chegam depois. O foco caía onde desse, e a
    // pessoa continuava fora do formulário.
    //
    // Então: tenta agora e, se ainda não há campo, fica de olho até haver —
    // com prazo, porque modal que nunca carrega não pode prender o foco para
    // sempre.
    const temCampo = () =>
      focaveisDe(caixa).some(e => /^(INPUT|SELECT|TEXTAREA)$/.test(e.tagName));

    const observador = new MutationObserver(() => {
      if (!temCampo()) return;
      observador.disconnect();
      entrarComOFoco(caixa);
    });

    // Nada de `requestAnimationFrame` aqui: ele não é garantia de execução —
    // o navegador o suspende quando o quadro não está sendo pintado, e o foco
    // simplesmente não entrava (visto em 14/09/2026 medindo a ficha dentro de
    // um quadro de teste). Tarefa de temporizador sempre roda.
    entrarComOFoco(caixa);
    setTimeout(() => {
      if (temCampo()) {
        entrarComOFoco(caixa);
        return;
      }
      observador.observe(caixa, { childList: true, subtree: true });
      setTimeout(() => observador.disconnect(), 1500);
    }, 0);

    document.addEventListener('keydown', aoTeclar, true);

    abertos.set(caixa, { aoTeclar, focoAnterior, observador, guarda });
  },

  beforeUnmount(caixa) {
    const estado = abertos.get(caixa);
    if (!estado) return;
    document.removeEventListener('keydown', estado.aoTeclar, true);
    estado.observador?.disconnect();
    estado.guarda?.soltar();
    // A DEVOLUÇÃO DO FOCO é a parte que todo modal escrito à mão esquece.
    // Sem ela o foco volta para o começo do documento, e quem navega por
    // teclado precisa percorrer a tela inteira de novo para voltar de onde
    // saiu.
    estado.focoAnterior?.focus?.();
    abertos.delete(caixa);
  },
};

export default modalAcessivel;
